import {BrowserModule} from '@angular/platform-browser';
import {
    ErrorHandler,
    inject,
    Injector,
    LOCALE_ID,
    NgModule,
    provideAppInitializer
} from '@angular/core';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {LoginComponent} from './login/login.component';
import {HeaderComponent} from './app-main/header/header.component';
import {FooterComponent} from './footer/footer.component';
import {TeamDecisionsModule} from './app-main/team-decisions/team-decisions.module';
import {HttpClient, HttpHeaders, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {registerLocaleData} from '@angular/common';
import {provideTranslateService, TranslateLoader, TranslatePipe} from '@ngx-translate/core';
import localeFr from '@angular/common/locales/fr';
import localeFrExtra from '@angular/common/locales/fr';
import {FormsModule} from '@angular/forms';
import {MainMenuComponent} from './app-main/menu/main-menu.component';
import {AppMainComponent} from './app-main/app-main.component';
import {ForgottenPasswordPopupComponent} from './login/forgotten-password-popup/forgotten-password-popup.component';
import {SupportPopupComponent} from './app-main/menu/support-popup/support-popup.component';
import {ComponentsModule} from './components/components.module';
import {provideCharts, withDefaultRegisterables} from 'ng2-charts';
import {ContextModule} from './context/context.module';
import {ToastrModule} from 'ngx-toastr';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {LocatorService} from './locator.service';
import {AccountComponent} from './app-main/account/account.component';
import {IsLoggedInGuard} from './guard/is-logged-in.guard';
import {SchedulePopupComponent} from './app-main/schedule-popup/schedule-popup.component';
import {RankingComponent} from './app-main/ranking/ranking.component';
import {PipesModule} from './pipes/pipes.module';
import {EvaluationsModule} from './app-main/evaluations/evaluations.module';
import {MailBoxModule} from './app-main/mail-box/mail-box.module';
import {PeriodTimeComponent} from './app-main/header/period-time/period-time.component';
import {PasswordChangePopupComponent} from './password-change-popup/password-change-popup.component';
import {AreaCompetitionComponent} from './app-main/area-competition/area-competition.component';
import {RestaurantTypeRivalsComponent} from './app-main/area-competition/restaurant-type-rivals/restaurant-type-rivals.component';
import {AreaCompetitionHeaderComponent} from './app-main/area-competition/area-competition-header/area-competition-header.component';
import {ParametersModule} from './app-main/parameters/parameters.module';
import {IntroductionComponent} from './app-main/introduction/introduction.component';
import {AppMainModule} from './app-main/app-main.module';
import {UnauthorizedResponseInterceptor} from './unauthorized-response.interceptor';
import {ToolsModule} from './app-main/tools/tools.module';
import * as Sentry from '@sentry/angular';
import {Router} from '@angular/router';
import {I18nTranslateDirective} from "./components/i18n-translate.directive";

export function translateLoaderFactory(http: HttpClient) {
    return {
        getTranslation(lang: string): any {
            return http.get('/api/i18n/labels.json',
                {headers: new HttpHeaders({'Accept-Language': lang})});
        }
    };
}

registerLocaleData(localeFr, 'fr-FR', localeFrExtra);

@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        HeaderComponent,
        PeriodTimeComponent,
        FooterComponent,
        MainMenuComponent,
        AppMainComponent,
        ForgottenPasswordPopupComponent,
        SupportPopupComponent,
        AccountComponent,
        IntroductionComponent,
        SchedulePopupComponent,
        RankingComponent,
        PasswordChangePopupComponent,
        AreaCompetitionComponent,
        RestaurantTypeRivalsComponent,
        AreaCompetitionHeaderComponent
    ],
    bootstrap: [AppComponent], imports: [ToastrModule.forRoot({
            enableHtml: true,
            positionClass: 'toast-bottom-right',
            closeButton: true
        }),
        BrowserAnimationsModule,
        BrowserModule,
        AppRoutingModule,
        FormsModule,
        TeamDecisionsModule,
        ComponentsModule,
        ContextModule,
        PipesModule,
        EvaluationsModule,
        MailBoxModule,
        ParametersModule,
        AppMainModule,
        ToolsModule,
        TranslatePipe], providers: [
        { provide: LOCALE_ID, useValue: 'fr-FR' },
        { provide: HTTP_INTERCEPTORS, useClass: UnauthorizedResponseInterceptor, multi: true },
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: translateLoaderFactory,
                deps: [HttpClient]
            }
        }),
        IsLoggedInGuard,
        provideHttpClient(withInterceptorsFromDi()),
        provideCharts(withDefaultRegisterables()),
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
        { provide: Sentry.TraceService, deps: [Router] },
        provideAppInitializer(() => { inject(Sentry.TraceService); })
    ] })
export class AppModule {
    constructor(private injector: Injector) {
        LocatorService.injector = this.injector;
    }
}
